import {
  getUserDetails,
  isAdminUser,
  resolveSessionAgentId,
  resolveSessionIdSync,
} from "./authService";
import { buildCampaignBaggageQuoteItems } from "./belvedereCampaignBaggage";
import { BELVEDERE_CAMPAIGN_SLUG } from "./belvedereCampaignOrder";
import { buildAgentPublicOfferBranding } from "../utils/buildAgentPublicOfferBranding";
import { belvedereContract } from "../data/belvedereContractData";
import { royalTulipTbilisiContract } from "../data/royalTulipTbilisiContractData";
import {
  ROYAL_ZANZIBAR_CAMPAIGN_SLUG,
  royalZanzibarBeachResortContract,
} from "../data/royalZanzibarBeachResortContractData";
import {
  classifyBelvedereCampaignOccupancyPassengers,
  getStayNights,
  normalizeBelvedereCampaignRooms,
} from "./belvederePricing";
import {
  RECENT_SEARCH_CATEGORIES,
  BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT,
} from "../utils/agentRecentSearchPayload";

export const BELVEDERE_CAMPAIGN_PUBLIC_PATH = "/he/campaigns/royal-belvedere-2026";
export const BELVEDERE_BOOKING_PUBLIC_PATH = "/he/campaigns/royal-belvedere-booking";
export const ROYAL_TULIP_CAMPAIGN_SLUG = "royal-tulip-tbilisi-2026";
export const ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH = "/he/campaigns/royal-tulip-tbilisi-2026";
export const ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH =
  "/he/campaigns/royal-zanzibar-beach-resort-2026";
export {
  ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_ZANZIBAR_CAMPAIGN_SLUG,
};
export const BELVEDERE_CAMPAIGN_AGENT_SOURCE = "agent_dashboard";
export const BELVEDERE_CAMPAIGN_BOOKING_SOURCE = "campaign";
export const BELVEDERE_CAMPAIGN_AGENT_ID_PARAM = "agentId";

/**
 * Snapshot of agent contact/branding for customer digital offers (no session required).
 * @returns {{ agent_branding?: object }}
 */
export function buildBelvedereCampaignAgentBrandingSnapshot(user = getUserDetails()) {
  const branding = buildAgentPublicOfferBranding(user);
  if (!branding) return {};
  return {
    agent_branding: {
      logoUrl: branding.logoUrl || null,
      agencyName: branding.agencyName || null,
      agentName: branding.agentName || null,
      phone: branding.phone || null,
      email: branding.email || null,
      whatsapp: branding.whatsapp || null,
    },
  };
}
function readReferringAgentId(searchParams) {
  if (!searchParams) return null;
  const raw =
    typeof searchParams.get === "function"
      ? searchParams.get(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM) ||
        searchParams.get("agent_id")
      : searchParams[BELVEDERE_CAMPAIGN_AGENT_ID_PARAM] || searchParams.agent_id;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Landing return URL for the booking page Γאפ preserves referring agentId when present.
 */
export function buildBelvedereBookingReturnPath(searchParams) {
  const params = new URLSearchParams();
  const agentId = readReferringAgentId(searchParams);
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  const source =
    typeof searchParams?.get === "function"
      ? String(searchParams.get("source") || "").trim()
      : "";
  if (source) params.set("source", source);
  const query = params.toString();
  return query ? `${BELVEDERE_BOOKING_PUBLIC_PATH}?${query}` : BELVEDERE_BOOKING_PUBLIC_PATH;
}

export function buildBelvedereCampaignShareUrl(agentId) {
  const params = new URLSearchParams();
  const id = Number(agentId);
  if (Number.isFinite(id) && id > 0) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(id));
  }
  const query = params.toString();
  return query ? `${BELVEDERE_CAMPAIGN_PUBLIC_PATH}?${query}` : BELVEDERE_CAMPAIGN_PUBLIC_PATH;
}

export function buildBelvedereBookingShareUrl(agentId) {
  const params = new URLSearchParams();
  const id = Number(agentId);
  if (Number.isFinite(id) && id > 0) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(id));
  }
  const query = params.toString();
  return query ? `${BELVEDERE_BOOKING_PUBLIC_PATH}?${query}` : BELVEDERE_BOOKING_PUBLIC_PATH;
}

export function resolveCampaignSlugFromPathname(pathname = "") {
  const path = String(pathname || "");
  if (path.includes("royal-zanzibar")) return ROYAL_ZANZIBAR_CAMPAIGN_SLUG;
  if (path.includes("royal-tulip-tbilisi")) return ROYAL_TULIP_CAMPAIGN_SLUG;
  return BELVEDERE_CAMPAIGN_SLUG;
}

export function resolveCampaignPublicPath(slug) {
  if (slug === ROYAL_ZANZIBAR_CAMPAIGN_SLUG) return ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH;
  if (slug === ROYAL_TULIP_CAMPAIGN_SLUG) return ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH;
  return BELVEDERE_CAMPAIGN_PUBLIC_PATH;
}

export function buildRoyalTulipCampaignShareUrl(agentId) {
  const params = new URLSearchParams();
  const id = Number(agentId);
  if (Number.isFinite(id) && id > 0) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(id));
  }
  const query = params.toString();
  return query ? `${ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH}?${query}` : ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH;
}

export function buildRoyalTulipCampaignAgentDashboardUrl() {
  const agentId = resolveSessionAgentId();
  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  return `${ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}

export function openRoyalTulipCampaignFromAgentDashboard() {
  const url = buildRoyalTulipCampaignAgentDashboardUrl();
  if (typeof window === "undefined") return url;
  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}

export function buildRoyalZanzibarCampaignShareUrl(agentId) {
  const params = new URLSearchParams();
  const id = Number(agentId);
  if (Number.isFinite(id) && id > 0) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(id));
  }
  const query = params.toString();
  return query
    ? `${ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH}?${query}`
    : ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH;
}

export function buildRoyalZanzibarCampaignAgentDashboardUrl() {
  const agentId = resolveSessionAgentId();
  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  return `${ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}

export function openRoyalZanzibarCampaignFromAgentDashboard() {
  const url = buildRoyalZanzibarCampaignAgentDashboardUrl();
  if (typeof window === "undefined") return url;
  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}

export function canAccessAgentCampaignsDashboard(user = getUserDetails()) {
  return (
    Boolean(resolveSessionAgentId(user)) ||
    isAdminUser() ||
    Boolean(resolveSessionIdSync())
  );
}

export function buildBelvedereCampaignAgentDashboardUrl() {
  const agentId = resolveSessionAgentId();
  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  return `${BELVEDERE_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}

export function buildBelvedereBookingAgentDashboardUrl() {
  const agentId = resolveSessionAgentId();
  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  return `${BELVEDERE_BOOKING_PUBLIC_PATH}?${params.toString()}`;
}

function readSourceParam(searchParams) {
  if (!searchParams) return "";
  if (typeof searchParams.get === "function") {
    return String(searchParams.get("source") || "").trim();
  }
  return String(searchParams.source || "").trim();
}

/**
 * Entry attribution for campaign orders.
 * Public customers can carry ?agentId= on the link (referring agent).
 * Logged-in agents use session for dashboard pricing; URL agentId binds the order owner.
 */
export function resolveBelvedereCampaignEntryContext(searchParams) {
  const uiSourceParam = readSourceParam(searchParams);
  const user = getUserDetails();
  const sessionAgentId = resolveSessionAgentId(user);
  const referringAgentId = readReferringAgentId(searchParams);
  const admin = isAdminUser();
  const hasAgentSession = Boolean(sessionAgentId);
  const hasStaffSession = hasAgentSession || admin;

  let createdByRole = "public";
  if (admin) createdByRole = "admin";
  else if (hasAgentSession) createdByRole = "agent";

  let entrySource = "public";
  if (uiSourceParam === BELVEDERE_CAMPAIGN_AGENT_SOURCE && hasStaffSession) {
    entrySource = "agent_dashboard";
  } else if (referringAgentId) {
    entrySource = "agent_link";
  }

  const agent_id = hasAgentSession ? sessionAgentId : referringAgentId;

  const pathname =
    typeof window !== "undefined" ? String(window.location?.pathname || "") : "";
  const campaignSlug = resolveCampaignSlugFromPathname(pathname);

  return {
    booking_source: BELVEDERE_CAMPAIGN_BOOKING_SOURCE,
    campaign_slug: campaignSlug,
    campaign_public_path: resolveCampaignPublicPath(campaignSlug),
    entry_source: entrySource,
    created_by_role: hasStaffSession ? createdByRole : "public",
    agent_id: agent_id ?? null,
    referring_agent_id: referringAgentId,
    ui_source_param: uiSourceParam || null,
  };
}

export function buildBelvedereCampaignClientsInfo(baseClientsInfo = {}, entryContext) {
  const ctx = entryContext || resolveBelvedereCampaignEntryContext();
  const hotelCode = String(baseClientsInfo.hotel_code || "").trim();
  const campaignSlug =
    hotelCode === ROYAL_TULIP_CAMPAIGN_SLUG ||
    hotelCode === BELVEDERE_CAMPAIGN_SLUG ||
    hotelCode === ROYAL_ZANZIBAR_CAMPAIGN_SLUG
      ? hotelCode
      : ctx.campaign_slug || BELVEDERE_CAMPAIGN_SLUG;
  const selection =
    baseClientsInfo.campaign_selection && typeof baseClientsInfo.campaign_selection === "object"
      ? baseClientsInfo.campaign_selection
      : null;
  const selectedRoom =
    selection?.selected_room && typeof selection.selected_room === "object"
      ? selection.selected_room
      : null;
  const boardFromRoom = selectedRoom?.board || baseClientsInfo.board || null;
  const boardHeFromRoom = selectedRoom?.boardHe || baseClientsInfo.boardHe || null;
  return {
    ...baseClientsInfo,
    campaign: campaignSlug,
    booking_source: ctx.booking_source,
    campaign_slug: campaignSlug,
    entry_source: ctx.entry_source,
    created_by_role: ctx.created_by_role,
    ...(ctx.ui_source_param ? { ui_source_param: ctx.ui_source_param } : {}),
    ...(boardFromRoom ? { board: boardFromRoom } : {}),
    ...(boardHeFromRoom ? { boardHe: boardHeFromRoom } : {}),
  };
}

function isComboIdString(value) {
  return typeof value === "string" && /^\d+(?:-\d+)+$/.test(value.trim());
}

function isStrongPersistedRoomName(name) {
  const value = String(name || "").trim();
  if (!value) return false;
  if (/^חדר\s+\d+$/i.test(value)) return false;
  if (/^חבילת\s+\d+\s+חדרים$/i.test(value)) return false;
  return true;
}

/**
 * Compact per-room rows from quote.roomBreakdown — same source RoomOfferCard uses
 * (row.roomName / row.room.name, Hebrew kept on nameHe).
 */
export function compactPersistedRoomBreakdown(priceBreakdown) {
  const rows = Array.isArray(priceBreakdown?.roomBreakdown)
    ? priceBreakdown.roomBreakdown
    : Array.isArray(priceBreakdown)
      ? priceBreakdown
      : [];
  if (rows.length === 0) return null;
  return rows.map((row, index) => {
    const room = row?.room && typeof row.room === "object" ? row.room : null;
    const nameEn = row?.roomName || room?.name || row?.name || null;
    const nameHe = row?.roomNameHe || room?.nameHe || row?.nameHe || null;
    const displayName = nameEn || nameHe;
    const packageWeightedRaw = Number(
      row?.packageWeightedIls ?? row?.package_weighted_ils,
    );
    const packageWeightedIls =
      Number.isFinite(packageWeightedRaw) && packageWeightedRaw > 0
        ? Math.round(packageWeightedRaw * 100) / 100
        : null;
    return {
      roomIndex: row?.roomIndex ?? index + 1,
      roomCode: room?.code ?? row?.roomCode ?? row?.code ?? null,
      roomId: room?.id ?? row?.roomId ?? row?.id ?? null,
      name: displayName,
      nameHe: nameHe || displayName,
      displayNameHe: displayName,
      board: room?.board || row?.board || null,
      chargeType: room?.chargeType || row?.chargeType || null,
      occupancyLabel: row?.occupancyLabel || null,
      ...(packageWeightedIls != null ? { packageWeightedIls } : {}),
    };
  });
}

function nestedRoomsHaveTypeNames(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) return false;
  return rooms.every((row) =>
    isStrongPersistedRoomName(row?.name || row?.nameHe || row?.displayNameHe),
  );
}

/**
 * Prefer quote.roomBreakdown component names (RoomOfferCard source of truth).
 * Fall back to selectedRoom.rooms from toSelectedRoom.
 */
function resolvePersistedComponentRooms(selectedRoom, priceBreakdown) {
  const fromBreakdown = compactPersistedRoomBreakdown(priceBreakdown);
  if (fromBreakdown && nestedRoomsHaveTypeNames(fromBreakdown)) {
    return fromBreakdown;
  }
  const fromSelected = Array.isArray(selectedRoom?.rooms) ? selectedRoom.rooms : [];
  const mapSelectedRoomRow = (row, index) => {
    const packageWeightedRaw = Number(
      row?.packageWeightedIls ?? row?.package_weighted_ils,
    );
    const packageWeightedIls =
      Number.isFinite(packageWeightedRaw) && packageWeightedRaw > 0
        ? Math.round(packageWeightedRaw * 100) / 100
        : null;
    return {
      roomIndex: index + 1,
      roomCode: row.roomCode ?? row.code ?? null,
      roomId: row.roomId ?? row.id ?? null,
      name: row.name || row.nameHe || row.displayNameHe || null,
      nameHe: row.nameHe || row.name || row.displayNameHe || null,
      displayNameHe: row.displayNameHe || row.nameHe || row.name || null,
      board: row.board || null,
      chargeType: row.chargeType || null,
      occupancyLabel: row.occupancyLabel || null,
      ...(packageWeightedIls != null ? { packageWeightedIls } : {}),
    };
  };
  if (nestedRoomsHaveTypeNames(fromSelected)) {
    return fromSelected.map(mapSelectedRoomRow);
  }
  if (fromBreakdown && fromBreakdown.length > 0) return fromBreakdown;
  if (fromSelected.length > 0) {
    return fromSelected.map(mapSelectedRoomRow);
  }
  return null;
}

function buildComboIdFromComponentRooms(componentRooms, selectedRoom) {
  if (selectedRoom?.comboId && isComboIdString(selectedRoom.comboId)) {
    return selectedRoom.comboId;
  }
  if (selectedRoom?.combo_id && isComboIdString(selectedRoom.combo_id)) {
    return selectedRoom.combo_id;
  }
  if (!Array.isArray(componentRooms) || componentRooms.length < 2) return null;
  const codes = componentRooms
    .map((row) => row.roomCode)
    .filter((code) => code != null && Number.isFinite(Number(code)));
  if (codes.length !== componentRooms.length) return null;
  return codes.join("-");
}

/** Snapshot persisted on quote/order so customer offer page can show hotel+flights. */
export function buildBelvedereCampaignSelectionSnapshot(context = {}) {
  const room = context?.selectedRoom;
  const rooms = Array.isArray(context?.rooms)
    ? context.rooms.map((r) => ({
        adults: Number(r?.adults) || 0,
        children: Number(r?.children) || 0,
        childAges: Array.isArray(r?.childAges) ? r.childAges : [],
      }))
    : [];

  const baggageQuote = buildCampaignBaggageQuoteItems(
    context?.campaignExtras,
    context?.selectedFlights?.[0]?.rawDeal ?? null,
  );
  const componentRooms = resolvePersistedComponentRooms(room, context?.priceBreakdown);
  const comboId = buildComboIdFromComponentRooms(componentRooms, room);
  const componentNames = Array.isArray(componentRooms)
    ? componentRooms
        .map((row) => row.name || row.nameHe)
        .filter((name) => isStrongPersistedRoomName(name))
    : [];
  const packageLabelFromComponents =
    componentNames.length > 1 ? componentNames.join(" · ") : componentNames[0] || null;
  // Prefer real component names over generic package labels.
  const packageName =
    packageLabelFromComponents ||
    (isStrongPersistedRoomName(room?.name) && room.name) ||
    (isStrongPersistedRoomName(room?.nameHe) && room.nameHe) ||
    (isStrongPersistedRoomName(room?.displayNameHe) && room.displayNameHe) ||
    null;

  return {
    campaign_selection: {
      product_type: context?.productType || "hotel",
      arrival_date: context?.arrivalDate || null,
      departure_date: context?.departureDate || null,
      nights: context?.nights ?? null,
      flight_origin: context?.flightOrigin || null,
      rooms,
      hotel_code: context?.hotel?.hotelCode || context?.hotel?.slug || null,
      selected_room: room
        ? {
            roomCode: room.roomCode ?? room.code ?? null,
            roomId:
              room.roomId ||
              (typeof room.id === "string" && !isComboIdString(room.id) ? room.id : null) ||
              null,
            name: packageName,
            nameHe: packageName,
            displayNameHe: packageName,
            board: room.board || null,
            boardHe: room.boardHe || null,
            quantity:
              (Array.isArray(componentRooms) && componentRooms.length) ||
              room.quantity ||
              1,
            ...(comboId
              ? {
                  comboId,
                  componentCodes: comboId.split("-").map(Number),
                }
              : {}),
            ...(Array.isArray(componentRooms) && componentRooms.length > 0
              ? { rooms: componentRooms }
              : {}),
          }
        : null,
      ...(Array.isArray(componentRooms) && componentRooms.length > 0
        ? { room_breakdown: componentRooms }
        : {}),
      baggage_extras_total_ils: baggageQuote.totalIls || 0,
      baggage_lines: baggageQuote.lines,
    },
  };
}

export function openBelvedereCampaignFromAgentDashboard() {
  const url = buildBelvedereCampaignAgentDashboardUrl();
  if (typeof window === "undefined") return url;
  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}

export function openBelvedereBookingFromAgentDashboard() {
  const url = buildBelvedereBookingAgentDashboardUrl();
  if (typeof window === "undefined") return url;
  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}

export { BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT };

function tallyCampaignRoomsComposition(rooms) {
  let adults = 0;
  let children = 0;
  let infants = 0;
  const list = Array.isArray(rooms) ? rooms : [];
  list.forEach((room) => {
    const counts = classifyBelvedereCampaignOccupancyPassengers(room);
    adults += counts.adultCount;
    children += counts.childCount;
    infants += counts.infantCount;
  });
  return { adults, children, infants, rooms_count: list.length };
}

/**
 * POST /api/agent/recent-searches body for a Belvedere campaign search
 * (agent/admin session searching inside the campaign landing page).
 */
export function buildBelvedereCampaignRecentSearchPayload({
  arrivalDate,
  departureDate,
  rooms,
  includeFlights = false,
  flightOrigin = null,
  entryContext = null,
}) {
  const { adults, children, infants, rooms_count } = tallyCampaignRoomsComposition(rooms);
  const nights = getStayNights(arrivalDate, departureDate) || null;

  const raw_payload = {
    v: 1,
    variant: BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT,
    campaignSlug: BELVEDERE_CAMPAIGN_SLUG,
    arrivalDate: arrivalDate || null,
    departureDate: departureDate || null,
    rooms: normalizeBelvedereCampaignRooms(rooms),
    includeFlights: Boolean(includeFlights),
    flightOrigin: includeFlights ? flightOrigin || null : null,
    entrySource: entryContext?.entry_source ?? null,
  };

  return {
    category: includeFlights
      ? RECENT_SEARCH_CATEGORIES.VACATION_PACKAGES
      : RECENT_SEARCH_CATEGORIES.HOTELS,
    destination_label: `${belvedereContract.hotelNameHe} · ${belvedereContract.locationHe}`,
    destination_code: null,
    departure_date: arrivalDate || null,
    return_date: departureDate || null,
    nights,
    adults: adults || null,
    children: children || null,
    infants: infants || null,
    rooms_count: rooms_count || null,
    raw_payload,
  };
}

/**
 * "חפש שוב" for a campaign recent-search row → campaign landing URL that
 * restores dates/rooms/flights and auto-runs the search (agent dashboard entry).
 */
export function buildBelvedereCampaignRecentSearchRestoreUrl(rawPayload) {
  const raw = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  if (raw.variant !== BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT) return null;
  if (!raw.arrivalDate || !raw.departureDate) return null;

  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  const agentId = resolveSessionAgentId();
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  params.set("arrive", String(raw.arrivalDate));
  params.set("depart", String(raw.departureDate));
  if (Array.isArray(raw.rooms) && raw.rooms.length > 0) {
    params.set("rooms", JSON.stringify(raw.rooms));
  }
  if (raw.includeFlights) {
    params.set("flights", "1");
    if (raw.flightOrigin) params.set("origin", String(raw.flightOrigin));
  }
  return `${BELVEDERE_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}

export function buildRoyalTulipCampaignRecentSearchPayload(args) {
  const payload = buildBelvedereCampaignRecentSearchPayload(args);
  payload.destination_label = `${royalTulipTbilisiContract.hotelNameHe} · ${royalTulipTbilisiContract.locationHe}`;
  payload.raw_payload = {
    ...payload.raw_payload,
    variant: ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT,
    campaignSlug: ROYAL_TULIP_CAMPAIGN_SLUG,
  };
  return payload;
}

export function buildRoyalTulipCampaignRecentSearchRestoreUrl(rawPayload) {
  const raw = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  if (raw.variant !== ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT) return null;
  if (!raw.arrivalDate || !raw.departureDate) return null;

  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  const agentId = resolveSessionAgentId();
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  params.set("arrive", String(raw.arrivalDate));
  params.set("depart", String(raw.departureDate));
  if (Array.isArray(raw.rooms) && raw.rooms.length > 0) {
    params.set("rooms", JSON.stringify(raw.rooms));
  }
  if (raw.includeFlights) {
    params.set("flights", "1");
    if (raw.flightOrigin) params.set("origin", String(raw.flightOrigin));
  }
  return `${ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}

export function buildRoyalZanzibarCampaignRecentSearchPayload(args) {
  const payload = buildBelvedereCampaignRecentSearchPayload(args);
  payload.destination_label = `${royalZanzibarBeachResortContract.hotelNameHe} · ${royalZanzibarBeachResortContract.locationHe}`;
  payload.raw_payload = {
    ...payload.raw_payload,
    variant: ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT,
    campaignSlug: ROYAL_ZANZIBAR_CAMPAIGN_SLUG,
  };
  return payload;
}

export function buildRoyalZanzibarCampaignRecentSearchRestoreUrl(rawPayload) {
  const raw = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  if (raw.variant !== ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT) return null;
  if (!raw.arrivalDate || !raw.departureDate) return null;

  const params = new URLSearchParams();
  params.set("source", BELVEDERE_CAMPAIGN_AGENT_SOURCE);
  const agentId = resolveSessionAgentId();
  if (agentId != null) {
    params.set(BELVEDERE_CAMPAIGN_AGENT_ID_PARAM, String(agentId));
  }
  params.set("arrive", String(raw.arrivalDate));
  params.set("depart", String(raw.departureDate));
  if (Array.isArray(raw.rooms) && raw.rooms.length > 0) {
    params.set("rooms", JSON.stringify(raw.rooms));
  }
  if (raw.includeFlights) {
    params.set("flights", "1");
    if (raw.flightOrigin) params.set("origin", String(raw.flightOrigin));
  }
  return `${ROYAL_ZANZIBAR_CAMPAIGN_PUBLIC_PATH}?${params.toString()}`;
}
